import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import api, { formatApiErrorDetail } from "../lib/api";
import { MapPin, Clock as ClockIcon, CheckCircle, Camera, Warning, Briefcase } from "@phosphor-icons/react";
import { toast } from "sonner";
import StatusBadge from "../components/StatusBadge";

function formatTime(d) {
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}
function formatDate(d) {
  return d.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}
function isoToDate(s) {
  return s ? new Date(s) : null;
}

function friendlyGpsError(err) {
  if (!err) return "Unable to get location";
  if (err.code === 1) return "Location access denied. Enable GPS in your device settings.";
  if (err.code === 2) return "Your location could not be verified. Move to an open area and try again.";
  if (err.code === 3) return "GPS timed out. Move outside and retry.";
  return err.message || "Unable to get location";
}

export default function EmployeeClock() {
  const { user } = useAuth();
  const [now, setNow] = useState(new Date());
  const [coords, setCoords] = useState(null);
  const [gpsError, setGpsError] = useState("");
  const [today, setToday] = useState(null);
  const [store, setStore] = useState(null);
  const [allStores, setAllStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [shift, setShift] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [working, setWorking] = useState(false);
  const [photo, setPhoto] = useState("");
  const videoRef = useRef(null);
  const [camOpen, setCamOpen] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsError("Location access is required to record your attendance. Please enable GPS and try again.");
      return;
    }
    const watchId = navigator.geolocation.watchPosition(
      (p) => {
        setCoords({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy });
        setGpsError("");
      },
      (err) => setGpsError(friendlyGpsError(err)),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  const refresh = async () => {
    try {
      const [t, stores, shifts, asg] = await Promise.all([
        api.get("/attendance/today"),
        api.get("/stores"),
        api.get("/shifts"),
        api.get("/assignments/today-for-me").catch(() => ({ data: null })),
      ]);
      setToday(t.data);
      setAllStores(stores.data || []);
      const myStore = stores.data.find((s) => s.id === user.store_id);
      const myShift = shifts.data.find((s) => s.id === user.shift_id);
      setStore(myStore || null);
      setShift(myShift || null);
      setAssignment(asg.data || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line

  const needsStorePicker = !store && !assignment && allStores.length > 0;

  const handleClockIn = async () => {
    if (!coords) {
      toast.error("Waiting for GPS location…");
      return;
    }
    if (needsStorePicker && !selectedStoreId) {
      toast.error("Please select which store you're clocking in at.");
      return;
    }
    if (assignment && assignment.requires_photo && !photo) {
      toast.error("This assignment requires a photo. Tap Capture Photo first.");
      return;
    }
    setWorking(true);
    try {
      const payload = {
        latitude: coords.lat,
        longitude: coords.lng,
        accuracy: coords.acc,
        photo_data_url: photo || undefined,
      };
      if (needsStorePicker && selectedStoreId) payload.store_id = selectedStoreId;
      const { data } = await api.post("/attendance/clock-in", payload);
      setToday(data);
      setPhoto("");
      toast.success("Clocked in successfully");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setWorking(false);
    }
  };

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      setCamOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 100);
    } catch (e) {
      toast.error("Camera access denied");
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    setPhoto(canvas.toDataURL("image/jpeg", 0.7));
    // stop stream
    const stream = video.srcObject;
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setCamOpen(false);
  };

  const handleClockOut = async () => {
    if (!coords) {
      toast.error("Waiting for GPS location…");
      return;
    }
    setWorking(true);
    try {
      const { data } = await api.post("/attendance/clock-out", {
        latitude: coords.lat,
        longitude: coords.lng,
        accuracy: coords.acc,
      });
      setToday(data);
      toast.success("Clocked out successfully");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setWorking(false);
    }
  };

  const hasClockedIn = today && today.clock_in_at;
  const hasClockedOut = today && today.clock_out_at;
  const action = hasClockedOut ? "done" : hasClockedIn ? "clockout" : "clockin";

  const inDt = today?.clock_in_at ? isoToDate(today.clock_in_at) : null;
  const outDt = today?.clock_out_at ? isoToDate(today.clock_out_at) : null;

  return (
    <div className="px-4 py-8 md:p-10 max-w-2xl mx-auto" data-testid="employee-clock-page">
      <div className="mb-8">
        <div className="label-eyebrow">{formatDate(now)}</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter text-zinc-950 mt-2">
          Hello, {user?.name?.split(" ")[0] || "there"}
        </h1>
        <p className="text-zinc-600 mt-2">
          {shift ? `Shift: ${shift.start_time} – ${shift.end_time}` : "No shift assigned"}
        </p>
      </div>

      {/* Off-site assignment banner */}
      {assignment && (
        <div className="mb-6 bg-blue-50 border-l-4 border-klein rounded-sm p-4" data-testid="assignment-banner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-klein text-white grid place-items-center rounded-sm flex-shrink-0">
              <Briefcase size={20} weight="bold" />
            </div>
            <div className="flex-1">
              <div className="label-eyebrow text-klein">Off-site assignment today</div>
              <div className="font-heading font-bold text-lg text-zinc-950 mt-0.5">{assignment.name}</div>
              <div className="text-sm text-zinc-700">{assignment.address}</div>
              {assignment.reference_number && (
                <div className="text-xs font-mono text-zinc-600 mt-1">Ref: {assignment.reference_number}</div>
              )}
              {assignment.note && (
                <div className="text-xs text-zinc-600 mt-1 italic">Note: {assignment.note}</div>
              )}
              {assignment.requires_photo && (
                <div className="text-xs text-amber-700 mt-2 flex items-center gap-1">
                  <Camera size={12} weight="bold" /> Photo required at clock-in
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Store picker — shown when user has no assigned home store & no off-site assignment */}
      {needsStorePicker && action === "clockin" && (
        <div className="mb-6 bg-amber-50 border-l-4 border-amber-400 rounded-sm p-4" data-testid="store-picker-block">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-400 text-zinc-950 grid place-items-center rounded-sm flex-shrink-0">
              <Warning size={20} weight="bold" />
            </div>
            <div className="flex-1">
              <div className="label-eyebrow text-amber-800">No home store assigned</div>
              <div className="text-sm text-zinc-800 mt-0.5 mb-3">
                Pick where you&apos;re clocking in today. An admin can set a default store for you on the Users page.
              </div>
              <label htmlFor="clock-store-picker" className="block label-eyebrow mb-1.5">Store</label>
              <select
                id="clock-store-picker"
                data-testid="clock-store-picker"
                value={selectedStoreId}
                onChange={(e) => setSelectedStoreId(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-zinc-300 rounded-sm text-sm focus:outline-none focus:border-zinc-950"
              >
                <option value="">Select a store…</option>
                {allStores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.address ? ` — ${s.address}` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* No stores at all — first-time company */}
      {!store && !assignment && allStores.length === 0 && action === "clockin" && (
        <div className="mb-6 bg-zinc-100 border border-zinc-300 rounded-sm p-4" data-testid="no-stores-empty-state">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-zinc-950 text-white grid place-items-center rounded-sm flex-shrink-0">
              <MapPin size={20} weight="bold" />
            </div>
            <div className="flex-1">
              <div className="label-eyebrow text-zinc-700">No stores set up yet</div>
              <div className="text-sm text-zinc-800 mt-1">
                Your company hasn&apos;t added any stores. Ask an admin to add one on the <span className="font-semibold">Stores</span> page before clocking in.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Live time */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-6 grain-overlay relative overflow-hidden">
        <div className="label-eyebrow">Current Time</div>
        <div data-testid="live-clock" className="font-heading font-black text-5xl tracking-tighter text-zinc-950 mt-1 tabular-nums">
          {formatTime(now)}
        </div>
        <div className="mt-4 flex items-center gap-2 text-sm">
          <MapPin weight="fill" size={14} className={coords ? "text-emerald-600" : "text-zinc-400"} />
          <span data-testid="gps-status" className="text-zinc-700 font-mono">
            {coords
              ? `GPS locked · ±${Math.round(coords.acc)}m`
              : gpsError || "Acquiring GPS…"}
          </span>
        </div>
      </div>

      {/* Action button */}
      <div className="flex flex-col items-center py-6">
        {/* Photo capture area */}
        {assignment && assignment.requires_photo && action === "clockin" && (
          <div className="w-full max-w-sm mb-6 bg-white border border-zinc-200 rounded-sm p-4">
            <div className="label-eyebrow mb-2 flex items-center gap-1"><Camera size={12} weight="bold" /> Photo verification</div>
            {photo ? (
              <div>
                <img src={photo} alt="clock-in verification" className="w-full rounded-sm mb-2" data-testid="captured-photo" />
                <button onClick={() => { setPhoto(""); openCamera(); }} className="text-xs underline text-zinc-700">Retake</button>
              </div>
            ) : camOpen ? (
              <div>
                <video ref={videoRef} className="w-full rounded-sm mb-2 bg-black" muted playsInline />
                <button onClick={capturePhoto} className="w-full py-2 bg-zinc-950 text-white text-sm rounded-sm" data-testid="capture-photo-btn">
                  📸 Capture
                </button>
              </div>
            ) : (
              <button onClick={openCamera} className="w-full py-3 border border-dashed border-zinc-400 text-sm rounded-sm hover:bg-zinc-50" data-testid="open-camera-btn">
                <Camera size={16} weight="bold" className="inline mr-2" /> Open camera
              </button>
            )}
          </div>
        )}

        {action === "clockin" && (
          <button
            data-testid="clock-in-button"
            onClick={handleClockIn}
            disabled={
              working ||
              !coords ||
              (allStores.length === 0 && !assignment) ||
              (needsStorePicker && !selectedStoreId)
            }
            className="relative w-56 h-56 rounded-full bg-zinc-950 hover:bg-zinc-800 disabled:bg-zinc-300 transition-all shadow-xl group"
          >
            {coords && !working && (
              <span className="absolute inset-0 rounded-full bg-emerald-500/30 animate-pulse-ring" />
            )}
            <div className="relative flex flex-col items-center justify-center text-white">
              <ClockIcon size={40} weight="bold" />
              <div className="font-heading font-black text-2xl tracking-tight mt-3">
                {working ? "..." : "CLOCK IN"}
              </div>
              <div className="text-[0.65rem] tracking-[0.2em] uppercase text-zinc-400 mt-1">
                Tap when ready
              </div>
            </div>
          </button>
        )}

        {action === "clockout" && (
          <button
            data-testid="clock-out-button"
            onClick={handleClockOut}
            disabled={working || !coords}
            className="relative w-56 h-56 rounded-full bg-orange-600 hover:bg-orange-500 disabled:bg-zinc-300 transition-all shadow-xl"
          >
            <div className="flex flex-col items-center justify-center text-white">
              <ClockIcon size={40} weight="bold" />
              <div className="font-heading font-black text-2xl tracking-tight mt-3">
                {working ? "..." : "CLOCK OUT"}
              </div>
              <div className="text-[0.65rem] tracking-[0.2em] uppercase text-orange-100 mt-1">
                End your shift
              </div>
            </div>
          </button>
        )}

        {action === "done" && (
          <div className="w-56 h-56 rounded-full bg-emerald-50 border-4 border-emerald-500 grid place-items-center" data-testid="clock-done-indicator">
            <div className="flex flex-col items-center justify-center text-emerald-700">
              <CheckCircle size={48} weight="duotone" />
              <div className="font-heading font-black text-xl tracking-tight mt-3">DONE</div>
              <div className="text-[0.65rem] tracking-[0.2em] uppercase mt-1">See you tomorrow</div>
            </div>
          </div>
        )}
      </div>

      {/* Today's summary */}
      {today && (
        <div className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-200" data-testid="today-summary">
          <Row label="Status" value={<StatusBadge status={today.status} />} />
          <Row label="Store" value={today.store_name || "—"} />
          <Row
            label="Clock In"
            value={inDt ? formatTime(inDt) : "—"}
            sub={inDt && today.clock_in_distance_m != null ? `±${today.clock_in_distance_m}m from store` : null}
          />
          <Row
            label="Clock Out"
            value={outDt ? formatTime(outDt) : "—"}
            sub={today.worked_minutes ? `${Math.floor(today.worked_minutes/60)}h ${today.worked_minutes%60}m worked` : null}
          />
          {today.late_minutes > 0 && (
            <Row label="Late by" value={`${today.late_minutes} min`} />
          )}
          {today.overtime_minutes > 0 && (
            <Row label="Overtime" value={`${today.overtime_minutes} min`} />
          )}
        </div>
      )}

      {/* Store info */}
      {store && !assignment && (
        <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-5">
          <div className="label-eyebrow mb-2">Assigned Location</div>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-heading font-semibold text-lg">{store.name}</div>
              <div className="text-sm text-zinc-600">{store.address}</div>
            </div>
            <div className="text-right">
              <div className="label-eyebrow">Radius</div>
              <div className="font-mono text-sm">{store.radius_meters}m</div>
            </div>
          </div>
        </div>
      )}

      {/* Privacy notice */}
      <div className="mt-6 text-xs text-zinc-500 leading-relaxed" data-testid="privacy-notice">
        🔒 Your GPS location is recorded only at clock-in and clock-out for attendance verification.
        The server timestamp is authoritative — your device clock is not used.
      </div>
    </div>
  );
}

function Row({ label, value, sub }) {
  return (
    <div className="flex items-center justify-between px-5 py-3">
      <div className="text-sm label-eyebrow">{label}</div>
      <div className="text-right">
        <div className="text-sm font-semibold text-zinc-950">{value}</div>
        {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}
